/**
 * @fileoverview Hook actions layer for managing customer integrations (Hubspot) in Inbox.
 * @module useCustomerIntegrationsActions
 * @category Hook - Actions Layer
 * @since 3.1.0
 * @version 3.1.1
 */

import { useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import customersModelMc from '../../../models/CustomersModelMc';
import { LOAD_DATA_FAILURE } from '../../../../actions/inbox/conversationActions';
import { updateCustomerData } from '../../../../actions/inbox/customerDataActions';
import { showInboxApiErrorToast } from '../utils/showInboxApiErrorToast';

/**
 * Hook actions layer for managing Hubspot integrations in CustomerIntegrationsMC.
 *
 * - Only handles business operations and loading state.
 * - No contains UI logic or presentation.
 * - Optimized with useCallback and robust error handling.
 *
 * @param {Object} params - Initialization parameters
 * @param {string|number} params.customerId - Customer ID
 * @param {boolean} params.hs_active - Current state of Hubspot integration
 * @param {function} params.fetchCustomerData - Callback to reload customer data
 * @param {string|number} params.hs_id - Hubspot ID
 * @param {function} params.t - Translation function i18n (i18n)
 * @returns {Object} loading, hubspotButtons
 */
export default function useCustomerIntegrationsActions({
  customerId, hs_active, fetchCustomerData, hs_id, t
}) {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  /**
   * Toggles the Hubspot integration state for the customer.
   *
   * @memberof useCustomerIntegrationsActions
   * @async
   * @returns {Promise<void>} - Promise to toggle the Hubspot integration state
   */
  const handleToggleHubspotSync = useCallback(async () => {
    try {
      setLoading(true);
      const response = await customersModelMc.save({
        id: customerId,
        hs_active: !hs_active
      });
      if (response?.customer) {
        dispatch(updateCustomerData(response.customer));
      } else if (typeof fetchCustomerData === 'function') {
        fetchCustomerData();
      }
    } catch (error) {
      const errorMessage = showInboxApiErrorToast(
        error,
        t('integrations.hubspot_update_error', { defaultValue: 'Error updating HubSpot integration' })
      );
      dispatch({ type: LOAD_DATA_FAILURE, error: errorMessage });
    } finally {
      setLoading(false);
    }
  }, [customerId, hs_active, dispatch, fetchCustomerData, t]);

  // Hubspot integration buttons logic (UI Layer delegated from the component)
  const hubspotButtons = useMemo(() => {
    if (!t) return [];
    if (!ENV.HUBSPOT_INTEGRATED) {
      return [
        {
          text: t('integrations.connect_hubspot'),
          onClick: () => { document.location.href = ENV.HUBSPOT_AUTH_URL; },
          outlined: true,
          className: '!tw-bg-white !tw-text-blue-500'
        }
      ];
    }
    if (hs_active) {
      return [
        {
          icon: loading ? 'sync-outline' : null,
          text: t('integrations.unsync_contact'),
          onClick: handleToggleHubspotSync,
          outlined: true,
          className: '!tw-bg-white !tw-text-blue-500',
          disabled: loading
        },
        {
          text: t('integrations.view_in_hubspot'),
          onClick: () => window.open(`https://app.hubspot.com/contacts/${ENV.HS_ID}/contact/${hs_id}/`, '_blank'),
          outlined: false
        }
      ];
    }
    // Hubspot integrated but not synced
    return [
      {
        icon: loading ? 'sync-outline' : null,
        text: t('integrations.sync_contact'),
        onClick: handleToggleHubspotSync,
        outlined: true,
        className: '!tw-bg-white !tw-text-blue-500',
        disabled: loading
      }
    ];
  }, [hs_active, loading, handleToggleHubspotSync, t, hs_id]);

  return {
    loading,
    hubspotButtons
  };
}
